// Signal Lost Screen Component
// Terminal-aesthetic transition screen for video player
// Shows between video plays with static noise, glitches, and cryptic text

import { gsap } from 'gsap';
import { prefersReducedMotion } from '../scripts/animations';
import { glitchBurst, glitchRGBSplit, glitchText } from '../scripts/effects';

/**
 * Signal Lost screen configuration options
 */
export interface SignalLostOptions {
  /** Duration of the signal lost screen in ms (default: 2000) */
  duration?: number;
  /** Whether to include static noise overlay (default: true) */
  showStatic?: boolean;
  /** Whether to show glitch effects (default: true) */
  enableGlitches?: boolean;
  /** Primary message to display (default: "> SIGNAL LOST") */
  primaryMessage?: string;
  /** Secondary message to display (default: "RECONNECTING...") */
  secondaryMessage?: string;
  /** Additional cryptic messages to cycle through */
  crypticMessages?: string[];
  /** Callback when signal lost animation completes */
  onComplete?: () => void;
  /** Whether to show timestamp (default: true) */
  showTimestamp?: boolean;
  /** Intensity of static noise (1-10, default: 5) */
  staticIntensity?: number;
}

// Cryptic messages that cycle during the signal lost screen
const DEFAULT_CRYPTIC_MESSAGES = [
  'BUFFER OVERFLOW',
  'VECTOR MISALIGNMENT',
  'IDENTITY UNCERTAIN',
  'SIGNAL DEGRADED',
  'FRAME CORRUPTION',
  'TEMPORAL ANOMALY',
  'DATA FRAGMENT LOST',
  '??:??:??',
  '[REDACTED]',
  'NODE 7 OFFLINE',
];

/**
 * Signal Lost Screen Component
 * Creates a dramatic, terminal-styled "signal lost" overlay
 * for video player transitions.
 */
export class SignalLost {
  private container: HTMLElement;
  private element: HTMLElement | null = null;
  private options: Required<SignalLostOptions>;
  private animationTimeline: gsap.core.Timeline | null = null;
  private messageInterval: ReturnType<typeof setInterval> | null = null;
  private glitchInterval: ReturnType<typeof setInterval> | null = null;

  constructor(container: HTMLElement, options: SignalLostOptions = {}) {
    this.container = container;
    this.options = {
      duration: options.duration ?? 2000,
      showStatic: options.showStatic ?? true,
      enableGlitches: options.enableGlitches ?? true,
      primaryMessage: options.primaryMessage ?? '> SIGNAL LOST',
      secondaryMessage: options.secondaryMessage ?? 'RECONNECTING...',
      crypticMessages: options.crypticMessages ?? DEFAULT_CRYPTIC_MESSAGES,
      onComplete: options.onComplete ?? (() => {}),
      showTimestamp: options.showTimestamp ?? true,
      staticIntensity: options.staticIntensity ?? 5,
    };
  }

  /**
   * Generate a glitchy timestamp
   */
  private generateTimestamp(): string {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    const secs = String(now.getSeconds()).padStart(2, '0');

    // Occasionally corrupt the timestamp
    if (Math.random() < 0.3) {
      const glitchChars = '??░▓█▒';
      const corrupt = (s: string) => {
        return s.split('').map(c =>
          Math.random() < 0.3 ? glitchChars[Math.floor(Math.random() * glitchChars.length)] : c
        ).join('');
      };
      return `${corrupt(hours)}:${corrupt(mins)}:${corrupt(secs)}`;
    }
    return `${hours}:${mins}:${secs}`;
  }

  /**
   * Create the signal lost overlay element
   */
  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'signal-lost-overlay';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-label', 'Video signal lost, reconnecting');

    const timestamp = this.options.showTimestamp ? this.generateTimestamp() : '';

    el.innerHTML = `
      <div class="signal-lost-static" aria-hidden="true"></div>
      <div class="signal-lost-scanlines" aria-hidden="true"></div>
      <div class="signal-lost-vignette" aria-hidden="true"></div>

      <div class="signal-lost-content">
        <div class="signal-lost-header">
          <span class="signal-lost-indicator" aria-hidden="true">●</span>
          <span class="signal-lost-status">NO SIGNAL</span>
          ${timestamp ? `<span class="signal-lost-timestamp">${timestamp}</span>` : ''}
        </div>

        <div class="signal-lost-main">
          <div class="signal-lost-icon" aria-hidden="true">
            <div class="signal-bars">
              <span class="bar bar-1"></span>
              <span class="bar bar-2"></span>
              <span class="bar bar-3"></span>
              <span class="bar bar-4"></span>
            </div>
          </div>

          <div class="signal-lost-primary">${this.options.primaryMessage}</div>
          <div class="signal-lost-secondary">${this.options.secondaryMessage}</div>
          <div class="signal-lost-cryptic" aria-hidden="true"></div>
        </div>

        <div class="signal-lost-footer">
          <div class="signal-lost-progress">
            <div class="signal-lost-progress-bar"></div>
          </div>
          <div class="signal-lost-meta">
            <span class="meta-node">NODE: 7</span>
            <span class="meta-baud">2400 BAUD</span>
          </div>
        </div>
      </div>

      <div class="signal-lost-glitch-overlay" aria-hidden="true"></div>
    `;

    return el;
  }

  /**
   * Apply static noise animation
   */
  private animateStatic(): void {
    if (!this.element || prefersReducedMotion()) return;

    const staticEl = this.element.querySelector('.signal-lost-static') as HTMLElement;
    if (!staticEl) return;

    const intensity = this.options.staticIntensity / 10;

    // Animate static opacity and position
    gsap.to(staticEl, {
      backgroundPosition: `${Math.random() * 100}% ${Math.random() * 100}%`,
      opacity: intensity * (0.8 + Math.random() * 0.4),
      duration: 0.05,
      repeat: -1,
      yoyo: true,
      ease: 'steps(2)',
    });
  }

  /**
   * Cycle through cryptic messages
   */
  private cycleCrypticMessages(): void {
    if (!this.element) return;

    const crypticEl = this.element.querySelector('.signal-lost-cryptic') as HTMLElement;
    if (!crypticEl) return;

    const messages = this.options.crypticMessages;
    let index = 0;

    this.messageInterval = setInterval(() => {
      if (prefersReducedMotion()) {
        crypticEl.textContent = messages[index];
      } else {
        // Apply text glitch effect when changing message
        glitchText(crypticEl, 200);
        setTimeout(() => {
          crypticEl.textContent = messages[index];
        }, 100);
      }
      index = (index + 1) % messages.length;
    }, 400);
  }

  /**
   * Apply periodic glitch effects
   */
  private applyGlitchEffects(): void {
    if (!this.element || !this.options.enableGlitches || prefersReducedMotion()) return;

    const content = this.element.querySelector('.signal-lost-content') as HTMLElement;
    const glitchOverlay = this.element.querySelector('.signal-lost-glitch-overlay') as HTMLElement;

    // Random glitch bursts
    this.glitchInterval = setInterval(() => {
      if (Math.random() < 0.4) {
        glitchBurst(content, 2, 6);
      }
      if (Math.random() < 0.3) {
        glitchRGBSplit(content, 4, 0.15);
      }

      // Flash the glitch overlay occasionally
      if (Math.random() < 0.2 && glitchOverlay) {
        gsap.to(glitchOverlay, {
          opacity: 0.3 + Math.random() * 0.3,
          duration: 0.05,
          yoyo: true,
          repeat: 1,
        });
      }
    }, 200);
  }

  /**
   * Animate the progress bar
   */
  private animateProgressBar(): void {
    if (!this.element) return;

    const progressBar = this.element.querySelector('.signal-lost-progress-bar') as HTMLElement;
    if (!progressBar) return;

    gsap.fromTo(progressBar,
      { width: '0%' },
      {
        width: '100%',
        duration: this.options.duration / 1000,
        ease: 'none',
      }
    );
  }

  /**
   * Animate signal bars
   */
  private animateSignalBars(): void {
    if (!this.element || prefersReducedMotion()) return;

    const bars = this.element.querySelectorAll('.signal-bars .bar');

    bars.forEach((bar, i) => {
      gsap.to(bar, {
        opacity: Math.random() < 0.5 ? 0.2 : 1,
        duration: 0.1 + Math.random() * 0.2,
        repeat: -1,
        yoyo: true,
        delay: i * 0.1,
      });
    });
  }

  /**
   * Show the signal lost screen
   */
  show(): Promise<void> {
    return new Promise((resolve) => {
      // Create and insert element
      this.element = this.createElement();
      this.container.appendChild(this.element);

      // Start animations
      this.animateStatic();
      this.cycleCrypticMessages();
      this.applyGlitchEffects();
      this.animateProgressBar();
      this.animateSignalBars();

      // Entry animation
      this.animationTimeline = gsap.timeline();

      if (!prefersReducedMotion()) {
        this.animationTimeline
          .fromTo(this.element,
            { opacity: 0 },
            { opacity: 1, duration: 0.1 }
          )
          .fromTo(this.element.querySelector('.signal-lost-content'),
            { scale: 0.95, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.2 },
            '-=0.05'
          );
      } else {
        this.element.style.opacity = '1';
      }

      // Schedule hide
      setTimeout(() => {
        this.hide().then(resolve);
      }, this.options.duration);
    });
  }

  /**
   * Hide the signal lost screen
   */
  hide(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.element) {
        resolve();
        return;
      }

      // Clear intervals
      if (this.messageInterval) clearInterval(this.messageInterval);
      if (this.glitchInterval) clearInterval(this.glitchInterval);

      // Kill existing animations
      if (this.animationTimeline) {
        this.animationTimeline.kill();
      }
      gsap.killTweensOf(this.element.querySelectorAll('*'));

      // Exit animation
      if (!prefersReducedMotion()) {
        gsap.to(this.element, {
          opacity: 0,
          duration: 0.15,
          onComplete: () => {
            this.cleanup();
            this.options.onComplete();
            resolve();
          },
        });
      } else {
        this.cleanup();
        this.options.onComplete();
        resolve();
      }
    });
  }

  /**
   * Clean up the component
   */
  private cleanup(): void {
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
    this.animationTimeline = null;
  }

  /**
   * Force destroy the component
   */
  destroy(): void {
    if (this.messageInterval) clearInterval(this.messageInterval);
    if (this.glitchInterval) clearInterval(this.glitchInterval);
    if (this.animationTimeline) this.animationTimeline.kill();
    if (this.element) {
      gsap.killTweensOf(this.element.querySelectorAll('*'));
    }
    this.cleanup();
  }
}

/**
 * Convenience function to show signal lost screen
 */
export function showSignalLost(
  container: HTMLElement,
  options: SignalLostOptions = {}
): Promise<void> {
  const signalLost = new SignalLost(container, options);
  return signalLost.show();
}

/**
 * Default export for the component
 */
export default SignalLost;
